import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import { enqueueSnackbar } from '../components/Toast';
import { getAttachmentDownloadLinkFromResponse } from '../components/attachments';
import { OWNER_ID, REFERRER_LOCAL_STORAGE_KEY, ROUTE_CONSTANTS } from '../constants/constants';
import { useI18n } from '../i18n/I18n';
import useDefaultRoute from '../nav/useDefaultRoute';
import { ATTACHMENT_DOWNLOAD_URL } from '../utils/download';
import { ChangeRoute } from '../utils/useHistory';
import { UserContext } from './UserContext';
import { getToken, isTokenExpired, resetToken } from './api';

type RouteParams = {
    assetId?: string;
};

const openBlobResponse = async (response: Response) => {
    const sourceBlob = await response.blob();
    const blobUrl = window.URL.createObjectURL(sourceBlob);
    window.location.replace(blobUrl);
};

const storeCurrentPathAsReferrer = (pathname: string, search: string, hash: string) => {
    const referrerPayload = JSON.stringify({ pathname, search, hash });
    localStorage.setItem(REFERRER_LOCAL_STORAGE_KEY, referrerPayload);
};

const openPreviewResponse = async (response: Response) => {
    const attachmentDownloadLink = await getAttachmentDownloadLinkFromResponse(response);
    if (attachmentDownloadLink) {
        window.location.replace(attachmentDownloadLink);
        return;
    }

    await openBlobResponse(response);
};

const SecureAttachmentDownload = () => {
    const { assetId } = useParams<RouteParams>();
    const location = useLocation();
    const {
        isAuthenticated,
        userProfile,
        activeOrganizationAccount: activeOrganizationAccountFromContext
    } = useContext(UserContext);
    const { url } = useDefaultRoute();
    const t = useI18n();
    const [isBusy, setIsBusy] = useState(true);
    const [redirectPath, setRedirectPath] = useState<string | null>(null);

    const targetUrl = useMemo(() => {
        if (!assetId) {
            return '';
        }
        return `${ATTACHMENT_DOWNLOAD_URL}/${encodeURIComponent(assetId)}`;
    }, [assetId]);

    useEffect(() => {
        if (!isAuthenticated) {
            storeCurrentPathAsReferrer(location.pathname, location.search, location.hash);
        }
    }, [isAuthenticated, location.hash, location.pathname, location.search]);

    useEffect(() => {
        setRedirectPath(null);
    }, [assetId, location.search]);

    useEffect(() => {
        if (!isAuthenticated || !userProfile || !assetId || !targetUrl) {
            setIsBusy(false);
            return;
        }

        const abortController = new AbortController();
        let isMounted = true;

        (async () => {
            const token = getToken();
            const isTokenMissingOrExpired = !token || isTokenExpired();

            if (isTokenMissingOrExpired) {
                storeCurrentPathAsReferrer(location.pathname, location.search, location.hash);
                resetToken();
                setRedirectPath('/login');
                return;
            }

            const orgIdFromQuery = new URLSearchParams(location.search).get('orgId');
            const activeOrganizationAccount =
                orgIdFromQuery ||
                activeOrganizationAccountFromContext ||
                localStorage.getItem(ROUTE_CONSTANTS.lastSelectedOrgId);

            const headers = new Headers();
            headers.append('Authorization', `Bearer ${token.token}`);
            if (activeOrganizationAccount) {
                headers.append(OWNER_ID, activeOrganizationAccount);
            }

            try {
                const response = await fetch(targetUrl, {
                    method: 'GET',
                    headers,
                    signal: abortController.signal
                });

                if (response.status === 401) {
                    storeCurrentPathAsReferrer(location.pathname, location.search, location.hash);
                    resetToken();
                    setRedirectPath('/login');
                    return;
                }

                if (response.status === 403) {
                    enqueueSnackbar(t('attachment.not.available.to.preview'), { variant: 'error' });
                    setRedirectPath(url || '/login');
                    return;
                }

                if (!response.ok) {
                    enqueueSnackbar(t('attachment.not.available.to.preview'), { variant: 'error' });
                    setRedirectPath(url || '/login');
                    return;
                }

                await openPreviewResponse(response);
            } catch (error) {
                if ((error as Error)?.name === 'AbortError') {
                    return;
                }

                enqueueSnackbar(t('attachment.not.available.to.preview'), { variant: 'error' });
                setRedirectPath(url || '/login');
                return;
            } finally {
                if (isMounted) {
                    setIsBusy(false);
                }
            }
        })();

        return () => {
            isMounted = false;
            abortController.abort();
        };
    }, [
        activeOrganizationAccountFromContext,
        assetId,
        isAuthenticated,
        location.hash,
        location.pathname,
        location.search,
        t,
        targetUrl,
        url,
        userProfile
    ]);

    if (!isAuthenticated) {
        return <ChangeRoute path={'/login'} />;
    }

    if (!userProfile) {
        return <Loading />;
    }

    if (!assetId) {
        if (url) {
            return <ChangeRoute path={url} />;
        }

        return <ChangeRoute path={'/login'} />;
    }

    if (redirectPath) {
        return <ChangeRoute path={redirectPath} />;
    }

    return isBusy ? <Loading /> : null;
};

export default SecureAttachmentDownload;
